import {
  Accordion,
  AccordionItem,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@sainsburys-tech/fable'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApiClient } from '../../../common/AppContext/appContext'
import ConfirmationBanner from '../../../common/components/Banners/ConfirmationBanner'
import Loading from '../../../common/components/Loading'
import { addBannerMessage } from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { IResult } from '../../../common/types/IResult'
import { createBatchTemperatures, disposeBatch } from '../../api/productionApi'
import { TempCheckFor } from '../../enums/TempCheckFor'
import { BatchTray, Shelf } from '../../enums/TemperatureCheckPosition'
import { IBatch } from '../../types/IBatch'
import { IBatchItem } from '../../types/IBatchItem'
import { ITemperatureData } from '../../types/ITemperatureData'
import {
  getRequiredTemperatureCount,
  getRequiredTemperatureDgree,
  isOutOfOven,
} from '../../utils/hotFoodUtils'
import NoCounterTempsTakenModal from './NoCounterTempsTakenModal'
import TemperatureBoostAlertModal from './TemperatureBoostAlertModal'
import TemperatureCheckModal from './TemperatureCheckModal'

interface IProps {
  batch: IBatch
  batchIndex: number | undefined
  counterTemperaturesNotTaken: boolean
  removeBatch: (batchId: number) => void
  setErrorMessage: (message: string) => void
  updateTemperatures: (
    batchId: number,
    temperatures: ITemperatureData[],
  ) => void
  updateTemperaturesCount: (batchId: number, temperaturesCount: number) => void
}

const BatchCard = (props: IProps) => {
  const navigate = useNavigate()
  const { dispatch } = useContext(Context)
  const [isLoading, setIsLoading] = useState(false)
  const [isInputModalOpen, setIsInputModalOpen] = useState(false)
  const [isNoCounterTempsTakenModalOpen, setIsNoCounterTempsTakenModalOpen] =
    useState(false)
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)
  const [temperaturesCount, setTemperaturesCount] = useState(0)
  const [requiredTemperatureCount, setRequiredTemperatureCount] = useState(0)
  const [outOfOven, setOutOfOven] = useState(false)

  const apiClient = useApiClient()

  useEffect(() => {
    setTemperaturesCount(props.batch.temperaturesCount)
    setRequiredTemperatureCount(getRequiredTemperatureCount(props.batch))
    setOutOfOven(isOutOfOven(props.batch))
  }, [props.batch])

  const batchItems = (items: IBatchItem[]) => (
    <Table>
      <TableBody>
        {items.map((item, index) => (
          <TableRow key={index}>
            <TableCell>{item.skuName}</TableCell>
            <TableCell
              className='c-batch-card-produce-quantity'
              style={{ width: '30px' }}
            >
              {item.quantityProduced}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )

  const renderButtons = () => (
    <ButtonGroup orientation={'horizontal'}>
      {temperaturesCount === 0 && (
        <Button
          variant='secondary'
          onClick={() => navigate(`/hotfoodbatch/${props.batch.batchId}`)}
          style={{ backgroundColor: 'white' }}
          testid='edit-button'
        >
          Edit
        </Button>
      )}
      {!outOfOven && (
        <Button
          onClick={() =>
            props.counterTemperaturesNotTaken
              ? setIsNoCounterTempsTakenModalOpen(true)
              : setIsInputModalOpen(true)
          }
          testid='temp-check-button'
        >
          Add temp checks
        </Button>
      )}
    </ButtonGroup>
  )

  const handleSave = async (
    data: ITemperatureData[],
    isBluetoothReading: boolean,
  ) => {
    setIsInputModalOpen(false)

    const isValid = isValidTemperatures(data)
    setTemperaturesCount(temperaturesCount + 1)

    props.updateTemperaturesCount(props.batch.batchId, temperaturesCount + 1)

    if (!isValid) {
      setIsAlertModalOpen(true)
    }

    setIsLoading(true)
    const response = await createBatchTemperatures(apiClient, {
      batchId: props.batch.batchId,
      isBluetoothReading,
      temperatures: data.reduce(
        (
          accTs: Array<{
            position: BatchTray | Shelf
            temperature: number
          }>,
          t,
        ) => {
          if (t.temperature) {
            return [
              ...accTs,
              {
                position: t.position,
                temperature: Number(t.temperature),
              },
            ]
          }
          return accTs
        },
        [],
      ),
    })

    // ensure batch.temperatures are set to latest
    if (response.isSuccess) {
      props.updateTemperatures(props.batch.batchId, data)
    }

    setIsLoading(false)

    if (response.isSuccess && isValid) {
      // remove the batch from those displayed
      props.removeBatch(props.batch.batchId)
      dispatch(
        addBannerMessage(
          <ConfirmationBanner
            message={`The '${props.batch.programDescription}' batch has reached temperature.
          It has been marked as 'out of oven' at ${moment(new Date(Date.now())).format('hh.mm a')}.`}
          />,
        ),
      )
    }

    props.setErrorMessage(response.isSuccess ? '' : `${response.message}`)
    return
  }

  const isValidTemperatures = (data: ITemperatureData[]) => {
    const individualItemCount = props.batch.items.reduce(
      (acc, curr) => curr.quantityProduced * curr.portionSize + acc,
      0,
    )

    const validTemps = data.filter(
      (t) =>
        t.temperature !== '' &&
        Number(t.temperature) >=
          getRequiredTemperatureDgree(TempCheckFor.Batch),
    )
    return validTemps.length === Math.min(individualItemCount, 3)
  }

  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false)

    if (temperaturesCount >= 3) {
      // remove the batch from those displayed
      props.removeBatch(props.batch.batchId)
      disposeBatch(apiClient, {
        batchId: props.batch.batchId,
        storeId: props.batch.storeId,
      })
        .then((res: IResult) => {
          if (res.isSuccess) {
            dispatch(
              addBannerMessage(
                <ConfirmationBanner
                  message={`The '${props.batch.programDescription}' batch has been marked as 'disposed' at
                ${moment(new Date(Date.now())).format('hh.mm a')}.`}
                />,
              ),
            )
          }
        })
        .catch(() => props.setErrorMessage('An error has occurred.'))
    }
  }

  const getBatchCardTitleHead = () => {
    if (props.batch.isUserCreated) {
      return 'Additional batch -'
    }
    return `${moment.utc(props.batch.productionTime).local().format('HH:mm')}`
  }
  const renderTitle = () => {
    const showOut = outOfOven
    const showBoost = !outOfOven && props.batch.temperaturesCount > 0
    if (!showOut && !showBoost) return null
    return (
      <span className='c-batch-card-badges'>
        {showOut && (
          <Badge
            label='Out of oven'
            size='sm'
            variant='tertiary'
            type='green'
            testid='badge-out-of-oven'
          />
        )}
        {showBoost && (
          <Badge
            label='Boosted'
            size='sm'
            variant='tertiary'
            type='red'
            testid='badge-boosted'
          />
        )}
      </span>
    )
  }

  return (
    <>
      <Box className='c-batch-card production-batch-card'>
        <Accordion>
          <AccordionItem
            isDefaultOpen={false}
            key={props.batch.batchId}
            label={`${getBatchCardTitleHead()} ${props.batch.programDescription}`}
            labelComponent={renderTitle()}
            labelAs={'span'}
            testid='batch-card'
            size={'md'}
          >
            {batchItems(props.batch.items)}
          </AccordionItem>
        </Accordion>

        {renderButtons()}

        {isAlertModalOpen && (
          <TemperatureBoostAlertModal
            handleClose={handleCloseAlertModal}
            alertLevel={temperaturesCount}
            isOpen={isAlertModalOpen}
            handleAction={handleCloseAlertModal}
          />
        )}

        {isNoCounterTempsTakenModalOpen && (
          <NoCounterTempsTakenModal
            handleAction={() => navigate('/hotfoodcountertemperature')}
            handleClose={() => {
              setIsNoCounterTempsTakenModalOpen(false)
              setIsInputModalOpen(true)
            }}
            isOpen={isNoCounterTempsTakenModalOpen}
          />
        )}

        {isInputModalOpen && (
          <TemperatureCheckModal
            title={`Temperature checks for ${props.batch.programDescription}`}
            description={
              requiredTemperatureCount >= 3
                ? 'Take 3 readings, ideally from pieces at the top, middle and bottom of the oven.'
                : `Take ${requiredTemperatureCount} temperature reading(s).`
            }
            handleClose={() => setIsInputModalOpen(false)}
            handleSave={handleSave}
            requiredTemperatureCount={requiredTemperatureCount}
            isOpen={isInputModalOpen}
          />
        )}
      </Box>

      {isLoading && <Loading />}
    </>
  )
}

export default BatchCard
